import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getConfig } from '@/lib/config';
import {
  identifyAnalyticsUser,
  resetAnalytics,
  setAnalyticsCustomer,
  setAnalyticsSite,
} from '@/lib/analytics';
import type { AnalyticsConfig } from '@/types';

function readAnalyticsConfig(): AnalyticsConfig | undefined {
  try {
    return getConfig().analytics;
  } catch {
    // Config not loaded (tests, Storybook): analytics is simply off.
    return undefined;
  }
}

/**
 * Effect-only bridge from the auth context to product analytics (ADR-8).
 * Mounted once under `AuthShell`, so it sits inside both `AuthProvider` and
 * `DevAuthProvider` and no login path needs editing. The logout reset is
 * additionally called synchronously by both providers' `logout` callbacks;
 * `resetAnalytics` is idempotent, so the double call is harmless.
 */
export function AnalyticsIdentity(): null {
  const { isAuthenticated, user, activeSiteId } = useAuth();
  const userId = user?.userId;
  const identifiedUserRef = useRef<string | null>(null);
  const siteRef = useRef<string | null>(null);
  const wasAuthenticatedRef = useRef(false);

  // Logout and forced logout: authenticated -> not authenticated.
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      resetAnalytics();
      identifiedUserRef.current = null;
      siteRef.current = null;
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Login: one identify and one customer group per user (ref survives StrictMode re-runs).
  useEffect(() => {
    if (!isAuthenticated || !userId || identifiedUserRef.current === userId) return;
    identifiedUserRef.current = userId;
    void identifyAnalyticsUser(userId);
    const analytics = readAnalyticsConfig();
    if (analytics?.customerKey) setAnalyticsCustomer(analytics.customerKey, analytics.customerName);
  }, [isAuthenticated, userId]);

  // Site change. A null-to-null first mount is skipped as redundant.
  useEffect(() => {
    if (!isAuthenticated) return;
    const site = activeSiteId ?? null;
    if (siteRef.current === site) return;
    siteRef.current = site;
    setAnalyticsSite(site);
  }, [isAuthenticated, activeSiteId]);

  return null;
}
